/**
 * Sync: answer privately, lock in, nothing shows until you both have.
 *
 * The reveal is a server transition. This screen only ever renders what the API was willing to
 * say — there is no "hide until" branch anywhere below, because a one-sided answer never arrives
 * on this device in the first place.
 *
 * Master–detail: the rounds, and the open one. The master is an INDEX and is capped as one — a
 * round's row is a date, a progress line and two lock marks, and giving it half a desktop window
 * left eighteen hundred pixels of empty column under a single row.
 *
 * The reveal is the panel, not a door to it. It opens with the round's scoreline — "did I know
 * them" is the question the game asks, so the answer leads — and then every question is the prompt
 * on a card with both answers as chat bubbles beneath it, yours in your identity tint on the left
 * and theirs in theirs on the right.
 */
import { Chip } from '@/components/chip';
import { Empty } from '@/components/empty';
import { List, Row } from '@/components/list';
import { LockPair } from '@/components/lock-pair';
import { Panel } from '@/components/panel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHead, Garland } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { WhyThis } from '@/components/why-this';
import { dayLabel, localDay } from '@/lib/day';
import { otherPartner, partnerTint, type PartnerId } from '@/lib/partner';
import { cn } from '@/lib/utils';
import { api, ApiError } from '@/api';
import { LoadFailed, PanelSkeleton } from '@/components/load-state';
import { useLayout } from '@/useLayout';
import { usePoll } from '@/usePoll';
import { useStore } from '@/store';
import * as React from 'react';
import { ActivityIndicator, View } from 'react-native';

/** How wide the rounds index may get once the round is open beside it. */
const MASTER_MAX = 360;

interface SyncQuestion {
  id: string;
  type: 'compare' | 'predict';
  text: string;
  options: string[] | null;
  /**
   * Three short answers offered under a compare question, beside a box that still takes anything.
   *
   * Null on a predict, whose `options` already are the answers. Optional in the TYPE only, so a
   * client a deploy ahead of the API draws the plain box rather than a row of "undefined".
   */
  suggestions?: string[] | null;
  myAnswer: string | null;
  myPrediction: string | null;
  partnerAnswer?: string | null;
  partnerPrediction?: string | null;
  myHit?: boolean;
  partnerHit?: boolean;
}

interface SyncSession {
  id: string;
  status: 'open' | 'revealed';
  initiator: 'A' | 'B' | 'app';
  createdAt: string;
  revealedAt: string | null;
  myLocked: boolean;
  partnerLocked: boolean;
  /** The caller's own progress — never the partner's. Absent on an old payload. */
  questionCount?: number;
  answeredCount?: number;
  /** The scoreline, served on a REVEALED round only. */
  myHits?: number;
  partnerHits?: number;
  predictCount?: number;
  /** What one hit paid, off the server's scoring config. */
  predictHitPoints?: number;
  questions?: SyncQuestion[];
}

type Draft = { answer?: string; prediction?: string };

/** Where a round is, in the words the list row shows under its title. */
function statusLine(s: SyncSession, partnerName: string): string {
  if (s.status === 'revealed') return 'See how you did';
  if (s.myLocked && !s.partnerLocked) return `Waiting for ${partnerName}…`;
  if (!s.myLocked && s.partnerLocked) return 'They are locked in. Your move.';
  if (!s.myLocked) return 'Not answered yet.';
  return 'Both locked — revealing…';
}

/**
 * A round's own line of arithmetic — the second line of a row, and the thing the column was
 * missing entirely.
 *
 * Five rows reading "Open round" over "Not answered yet." is a list with no information in it: no
 * date, no idea which one is nearly done, no idea how any of them turned out. An open round says
 * how far the READER got (never the partner — a figure that moved while they typed would be the
 * one thing this panel promises it is not) and a revealed one says the scoreline.
 */
function progressLine(s: SyncSession, partnerName: string): string | null {
  if (s.status === 'revealed') {
    if (s.myHits === undefined || s.partnerHits === undefined) return null;
    return `You ${s.myHits} · ${partnerName} ${s.partnerHits} · ${s.predictCount ?? 0} predicts`;
  }
  if (s.questionCount === undefined || !s.questionCount) return null;
  return `${s.answeredCount ?? 0} of ${s.questionCount} answered`;
}

export function SyncScreen() {
  const { me, selected, select, invitesFor, acceptInvite, dismissInvite, refreshBadges, setSignalDraft, go, fail, look } =
    useStore();
  const activeId = selected.sync ?? null;
  const { wide } = useLayout();

  /**
   * Whose colour is whose. `me` is loaded before any panel renders (the shell waits on
   * `session: 'ready'`), so the fallback is a type-level floor rather than a state anybody sees —
   * and it is the identity half of the pairing, never a status, so nothing may be read off which
   * of the two a person got.
   */
  const mine: PartnerId = me?.partner ?? 'A';
  const theirs: PartnerId = otherPartner(mine);
  const partnerName = me?.partnerName ?? 'Them';

  const [sessions, setSessions] = React.useState<SyncSession[]>([]);
  const [active, setActive] = React.useState<SyncSession | null>(null);
  const [detailError, setDetailError] = React.useState<string | null>(null);
  const [drafts, setDrafts] = React.useState<Record<string, Draft>>({});
  const [busy, setBusy] = React.useState(false);
  const [starting, setStarting] = React.useState(false);
  const timers = React.useRef(new Map<string, ReturnType<typeof setTimeout>>());

  /**
   * The drafts as they are RIGHT NOW, for the debounced save and for the flush.
   *
   * The save used to close over `drafts` from the render that scheduled it, so a second keystroke
   * inside the same 400ms window saved the value from before the first one. A ref is read at the
   * moment the save runs, which is the only moment whose value is the right one.
   */
  const draftsRef = React.useRef(drafts);
  draftsRef.current = drafts;
  /** Question ids with a change that has not reached the server yet. What `flush` drains. */
  const unsaved = React.useRef(new Set<string>());

  /**
   * Revealed rounds this screen has already reported as opened. The list re-opens the active round
   * on every 15s poll, and a POST per poll would be a heartbeat rather than a receipt.
   */
  const seenRounds = React.useRef(new Set<string>());

  const open = React.useCallback(
    async (id: string) => {
      try {
        const s = await api.get<SyncSession>(`/api/sync/sessions/${id}`);
        setActive(s);
        setDetailError(null);
        // Opening the reveal is what clears its badge: locking first and never coming back is not
        // the same as having looked, and the payoff is the thing worth being reminded about. Its
        // own catch, and only its own — a bookkeeping POST that fails is not a reason to say the
        // round would not load when it is already on the screen.
        if (s.status === 'revealed' && !seenRounds.current.has(s.id)) {
          seenRounds.current.add(s.id);
          await api
            .post(`/api/sync/sessions/${s.id}/seen`)
            .then(() => refreshBadges())
            .catch(() => {});
        }
      } catch (e) {
        // ON THE PAGE, not swallowed. A tap that opened an empty column and said nothing was the
        // one failure mode nobody could tell from a slow network.
        setDetailError(e instanceof Error ? e.message : 'That round would not open.');
      }
    },
    [refreshBadges],
  );

  const load = React.useCallback(async () => {
    setSessions(await api.get<SyncSession[]>('/api/sync/sessions'));
    // Live-refresh a waiting screen so the reveal appears without a manual reload.
    if (activeId) await open(activeId);
  }, [activeId, open]);

  const { loaded, error, reload } = usePoll(load, 15_000);

  React.useEffect(() => {
    setDrafts({});
    unsaved.current.clear();
    setDetailError(null);
    if (!activeId) setActive(null);
    else {
      // The skeleton starts NOW, on the tap, rather than when the GET lands: a row that turns
      // selected beside an unchanged column reads as a press that did nothing.
      setActive(null);
      void open(activeId);
    }
  }, [activeId, open]);

  /** The one place an answer is written. Reads the ref, so it is never a stale keystroke. */
  const save = React.useCallback(
    async (question: SyncQuestion, sessionId: string) => {
      const draft = draftsRef.current[question.id];
      const answer = draft?.answer ?? question.myAnswer;
      const prediction = draft?.prediction ?? question.myPrediction;
      if (!answer) return;
      if (question.type === 'predict' && !prediction) return; // wait until both halves are picked
      await api
        .post(`/api/sync/sessions/${sessionId}/answers`, { questionId: question.id, answer, prediction })
        .then(() => unsaved.current.delete(question.id))
        .catch(() => undefined);
    },
    [],
  );

  /** Debounced upsert — answers persist as you type, so couch and phone both work. */
  const setDraft = (question: SyncQuestion, field: 'answer' | 'prediction', value: string) => {
    if (!activeId) return;
    setDrafts((d) => ({ ...d, [question.id]: { ...d[question.id], [field]: value } }));
    draftsRef.current = { ...draftsRef.current, [question.id]: { ...draftsRef.current[question.id], [field]: value } };
    unsaved.current.add(question.id);
    clearTimeout(timers.current.get(question.id));
    timers.current.set(
      question.id,
      setTimeout(() => void save(question, activeId), 400),
    );
  };

  /**
   * EVERY PENDING KEYSTROKE, ACTUALLY SAVED.
   *
   * Locking used to `await new Promise(r => setTimeout(r, 500))` and hope. That is not a flush: a
   * slow network makes it a race the lock wins, and the lost keystroke is the last thing somebody
   * typed before pressing the one button that cannot be undone. This cancels the timers and awaits
   * the writes themselves.
   */
  const flush = React.useCallback(async () => {
    if (!active?.questions || !activeId) return;
    for (const timer of timers.current.values()) clearTimeout(timer);
    timers.current.clear();
    const pending = active.questions.filter((q) => unsaved.current.has(q.id));
    await Promise.all(pending.map((q) => save(q, activeId)));
  }, [active, activeId, save]);

  const answered = (q: SyncQuestion) => {
    const d = drafts[q.id];
    const answer = d?.answer ?? q.myAnswer;
    const prediction = d?.prediction ?? q.myPrediction;
    return !!answer && (q.type !== 'predict' || !!prediction);
  };
  const allAnswered = !!active?.questions?.every(answered);

  /**
   * ONE OPEN ROUND AT A TIME, said before the press rather than after it.
   *
   * The server refuses a second round while this partner still owes an open one (409). The list
   * already carries the caller's own progress, so the button can say so up front — a disabled
   * control with a reason under it, which is what `Button` does with `reason`, instead of a press
   * that produces an error dialog. The catch below is the belt: a round answered on another device
   * between the poll and the tap is still the server's call, not this screen's.
   */
  const owedRound = sessions.find(
    (s) => s.status === 'open' && (s.answeredCount ?? 0) < (s.questionCount ?? 0),
  );

  const start = async () => {
    setStarting(true);
    try {
      const s = await api.post<SyncSession>('/api/sync/sessions');
      select('sync', s.id);
      await load();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && owedRound) select('sync', owedRound.id);
      else fail(e);
    } finally {
      setStarting(false);
    }
  };

  const lock = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await flush();
      setActive(await api.post<SyncSession>(`/api/sync/sessions/${active.id}/lock`));
      await Promise.all([load(), refreshBadges()]);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const invites = invitesFor('sync');

  const newRound = (
    <Button
      disabled={starting || !!owedRound}
      reason={owedRound ? 'Finish the open round first' : undefined}
      onPress={start}>
      <Text>New round</Text>
    </Button>
  );

  const list = (
    <>
      {invites.map((invite) => (
        <Card key={invite.id}>
          <CardContent>
            {/* Only the server knows whether a person or the engine opened this round, and it
                says so without saying which person. Naming the partner over a game-sent invite
                would credit them with something they never did. */}
            <CardHead>
              {invite.from === 'app' ? 'A new round is open' : `${me?.partnerName ?? 'They'} started a round`}
            </CardHead>
            <CardDescription>Answer privately — nothing shows until you both lock in.</CardDescription>
            <View className="flex-row flex-wrap gap-2">
              <Button
                onPress={async () => {
                  await acceptInvite(invite).catch(fail);
                  if (invite.refId) select('sync', invite.refId);
                }}>
                <Text>Open it</Text>
              </Button>
              <Button variant="ghost" flush={false} onPress={() => dismissInvite(invite).catch(fail)}>
                <Text>Not now</Text>
              </Button>
            </View>
          </CardContent>
        </Card>
      ))}

      {/**
       * Rounds are a list rather than a wall of cards, because the question asked of this column is
       * "which one, and is anyone waiting on me" — and the pair of lock markers only reads at a
       * glance when the rows are close enough together to compare down the column.
       */}
      {sessions.length ? (
        <List>
          {sessions.map((s, i) => {
            const progress = progressLine(s, partnerName);
            return (
              <Row
                key={s.id}
                first={i === 0}
                selected={activeId === s.id}
                onPress={() => select('sync', s.id)}
                title={
                  <View className="flex-row flex-wrap items-baseline gap-2">
                    <Text className="font-medium">{s.status === 'revealed' ? 'Revealed round' : 'Open round'}</Text>
                    {/* WHEN. A column of undated rows is a column nobody can navigate — and
                        `createdAt` is an instant, so it goes through `localDay` before the label
                        sees it. */}
                    <Text variant="small" className="text-muted-foreground">
                      {dayLabel(localDay(s.createdAt))}
                    </Text>
                  </View>
                }
                subtitle={
                  <View className="gap-0.5">
                    <Text variant="small" className="text-muted-foreground">
                      {statusLine(s, partnerName)}
                    </Text>
                    {progress ? (
                      <Text variant="small" className="text-foreground tabular-nums">
                        {progress}
                      </Text>
                    ) : null}
                  </View>
                }
                right={<LockPair me={mine} mine={s.myLocked} theirs={s.partnerLocked} size="sm" />}
              />
            );
          })}
        </List>
      ) : invites.length ? null : (
        <Empty
          mark="⇄"
          title="No rounds yet"
          body={`Start one — it invites ${me?.partnerName ?? 'your partner'} automatically, and nothing reveals until you have both locked in.`}
        />
      )}
    </>
  );

  const hitPoints = active?.predictHitPoints ?? 2;

  const detail = activeId ? (
    <>
      {/* Both sheets draw `.back` as a tracked uppercase LABEL rather than as a sentence you can
          press — a way out of a detail view, cut like the column heads around it. The arrow and
          the press are untouched; only the type is. */}
      <Button variant="ghost" size="sm" className="self-start" onPress={() => select('sync', null)}>
        <Text className="text-muted-foreground text-[13px] font-bold uppercase tracking-[0.8px]">← All rounds</Text>
      </Button>

      {detailError ? (
        <LoadFailed onRetry={() => void open(activeId)} reason={detailError} />
      ) : !active ? (
        <PanelSkeleton rows={3} />
      ) : active.status === 'open' && !active.myLocked ? (
        <>
          {(active.questions ?? []).map((q, i) => (
            <React.Fragment key={q.id}>
              <QuestionCard type={q.type} counter={`Question ${i + 1} of ${active.questions!.length}`} text={q.text}>
                {q.type === 'compare' ? (
                  <>
                    <Textarea
                      value={drafts[q.id]?.answer ?? q.myAnswer ?? ''}
                      onChangeText={(v) => setDraft(q, 'answer', v)}
                      placeholder="Your answer, in your words…"
                    />
                    {/**
                     * THREE TAPS, AND THE BOX IS STILL THE REAL ANSWER.
                     *
                     * "What does home mean to you in one sentence?" is a lovely question and an
                     * unanswerable one with a thumb on a bus, which is how rounds got abandoned
                     * half-answered. The chips are a way in — pressing one fills the box, where it
                     * can then be edited — and never a list to pick from, which is what the line
                     * beside them says out loud.
                     */}
                    {q.suggestions?.length ? (
                      <View className="gap-1.5">
                        <View className="flex-row flex-wrap gap-2">
                          {q.suggestions.map((s) => (
                            <Chip
                              key={`s-${s}`}
                              label={s}
                              selected={(drafts[q.id]?.answer ?? q.myAnswer) === s}
                              onPress={() => setDraft(q, 'answer', s)}
                            />
                          ))}
                        </View>
                        <Text variant="note">or type your own</Text>
                      </View>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Text variant="small" className="text-muted-foreground">
                      Your pick:
                    </Text>
                    <View className="flex-row flex-wrap gap-2">
                      {(q.options ?? []).map((opt) => (
                        <Chip
                          key={`a-${opt}`}
                          label={opt}
                          selected={(drafts[q.id]?.answer ?? q.myAnswer) === opt}
                          onPress={() => setDraft(q, 'answer', opt)}
                        />
                      ))}
                    </View>
                    <Text variant="small" className="text-muted-foreground">
                      Your guess for {partnerName}:
                    </Text>
                    <View className="flex-row flex-wrap gap-2">
                      {(q.options ?? []).map((opt) => (
                        <Chip
                          key={`p-${opt}`}
                          label={opt}
                          selected={(drafts[q.id]?.prediction ?? q.myPrediction) === opt}
                          onPress={() => setDraft(q, 'prediction', opt)}
                        />
                      ))}
                    </View>
                  </>
                )}
              </QuestionCard>
              {/* Once per round, under the FIRST question's answers — a footnote belongs to the
                  thing above it, and equidistant between two questions it read as a heading for
                  the next one. More air below than above says which. */}
              {i === 0 ? <WhyThis id="sync" className="pb-2" /> : null}
            </React.Fragment>
          ))}

          <Button disabled={!allAnswered || busy} onPress={lock}>
            <View className="flex-row items-center gap-2">
              {/* The lock flushes every pending keystroke before it posts, and that takes as long
                  as the network takes. A button that looks pressable while it works gets pressed
                  again. */}
              {busy ? <ActivityIndicator size="small" /> : null}
              <Text>{busy ? 'Locking in…' : 'Lock in — no edits after this'}</Text>
            </View>
          </Button>
        </>
      ) : active.status === 'open' ? (
        /**
         * LOCKED IS NOT A DEAD END.
         *
         * It used to be an ⏳ "Empty" — the component whose whole meaning is "there is nothing
         * here" — drawn over a round the reader had just spent five answers on. Their own answers
         * are theirs to read back at any time (the partner's are the ones that do not exist on
         * this device), so the round stays on screen, read-only, with a badge saying what it is
         * waiting for.
         */
        <>
          <Card>
            <CardContent className="gap-2">
              <Badge variant="success">
                <Text>Locked · waiting for {partnerName}</Text>
              </Badge>
              <CardDescription>
                Your answers are below, exactly as you left them. Nothing of {partnerName}’s is on this
                device until you have both locked in — you will get a ping when the reveal is ready.
              </CardDescription>
            </CardContent>
          </Card>

          {(active.questions ?? []).map((q, i) => (
            <QuestionCard
              key={q.id}
              type={q.type}
              counter={`Question ${i + 1} of ${active.questions!.length}`}
              text={q.text}>
              <Bubble who="You" tint={mine} side="mine" answer={q.myAnswer} hitPoints={hitPoints} />
              {q.type === 'predict' ? (
                <Text variant="small" className="text-muted-foreground self-start">
                  you guessed “{q.myPrediction ?? '—'}”
                </Text>
              ) : null}
            </QuestionCard>
          ))}
        </>
      ) : (
        <>
          <Scoreline
            session={active}
            partnerName={partnerName}
            look={look}
            hitPoints={hitPoints}
            /* THE ROUND HAS ONE GARLAND. It goes to the scoreline, which is the thing being read —
               unless the very first verdict below it is a HIT, in which case the hit takes it and
               the scoreline goes plain. Two strips a hundred pixels apart are not two moments,
               they are a repeating background. */
            moment={!(active.questions?.[0]?.type === 'predict' && active.questions[0]?.myHit)}
          />

          {(active.questions ?? []).map((q, i) => (
            <React.Fragment key={q.id}>
              <View className="gap-2">
                {/**
                 * Verdict first, over the question. On a predict the thing being read is whether
                 * you knew them — the two answers below are the evidence for it, not the headline.
                 *
                 * A HIT is a MOMENT: the garland sits above it, and it is the one moment the board
                 * draws on the bare ground rather than on a card. A MISS is the same block without
                 * the strip — a miss is worth reading and is not worth celebrating — but at the
                 * SAME display size, because a verdict drawn two steps smaller when it goes badly
                 * is a scoreboard that hides the losses. The word is Fraunces italic in Bouquet and
                 * the upright optical-144 cut in Tuxedo, which is the one place the two looks
                 * disagree about the same numeral. `fit-opsz` is web-only — native has no
                 * optical-size axis to move — so Tuxedo's verdict draws at Fraunces' default cut on
                 * a phone.
                 *
                 * Under it, WHOSE guess this was. Three "+2"s can be on one screen meaning three
                 * different things, so every one of them is owned by the words beside it.
                 */}
                {q.type === 'predict' ? (
                  <View className="gap-0.5 pb-1">
                    {q.myHit ? <Garland className="mx-3 mb-2" /> : null}
                    <Text
                      variant="display"
                      className={cn(
                        look === 'bouquet' ? 'italic' : 'fit-opsz',
                        q.myHit ? 'text-gold-text' : 'text-muted-foreground',
                      )}>
                      {q.myHit ? `HIT +${hitPoints}` : 'MISS'}
                    </Text>
                    <Text variant="small" className="text-muted-foreground">
                      you guessed “{q.myPrediction ?? '—'}”
                    </Text>
                  </View>
                ) : null}

                <QuestionCard type={q.type} text={q.text} />

                {/* Both answers, as the conversation they are. Never a hide-until branch: a
                    partner's answer is on this device only because the server already revealed the
                    round. */}
                <Bubble who="You" tint={mine} side="mine" answer={q.myAnswer} hitPoints={hitPoints} />
                <Bubble
                  who={partnerName}
                  tint={theirs}
                  side="theirs"
                  answer={q.partnerAnswer ?? null}
                  guess={q.type === 'predict' ? (q.partnerPrediction ?? null) : null}
                  hit={q.partnerHit}
                  hitPoints={hitPoints}
                />

                {q.partnerAnswer ? (
                  <Button
                    /* A GHOST. Five of these ride down a revealed round, one per question, and five
                       filled buttons is a column of controls with the answers between them. As a
                       word it is available without being the loudest thing on the page — and
                       `Button` gives a ghost the one accent ink that clears 4.5:1 at night, which
                       is what this was failing before: dark red on dark red, reading as disabled. */
                    variant="ghost"
                    size="sm"
                    className="self-start"
                    onPress={() => {
                      // A reveal is the one moment the app hands you something your partner
                      // actually wants, in their own words. Carrying it straight into the box is
                      // the shortest path from "they said that" to "and then it happened".
                      //
                      // « » around the quote, and they are load-bearing rather than typographic:
                      // the drafter is told that text inside them is the partner's own sentence
                      // and must never come back out, and the gateway rejects any step that
                      // reproduces five words of it. Without them a planted mission could quote
                      // an answer straight back at the person who typed it an hour earlier.
                      setSignalDraft({
                        kind: 'desire',
                        body: `${me?.partnerName ?? 'They'} said «${q.partnerAnswer}» — make it happen.`,
                      });
                      go('signals');
                    }}>
                    <Text>Make it a signal</Text>
                  </Button>
                ) : null}
              </View>
              {/* Once per round, under the first question's answers — the same placement the open
                  round gives it, and the same asymmetric air saying which question it belongs to. */}
              {i === 0 ? <WhyThis id="sync" className="pb-2" /> : null}
            </React.Fragment>
          ))}
        </>
      )}
    </>
  ) : undefined;

  if (!loaded)
    return (
      <Panel id="sync">
        <PanelSkeleton rows={3} />
      </Panel>
    );
  if (error)
    return (
      <Panel id="sync">
        <LoadFailed onRetry={reload} reason={error} />
      </Panel>
    );

  return (
    <Panel
      id="sync"
      detail={detail}
      masterMax={MASTER_MAX}
      /**
       * "New round" stays put while a round is open — on a WIDE layout, where the index it belongs
       * to is still fully on screen beside the detail. Below that the detail replaces the list, so
       * a button for the list would be a control for a thing that is not there.
       */
      actions={activeId && !wide ? undefined : newRound}>
      {list}
    </Panel>
  );
}

/**
 * THE ROUND'S SCORELINE — how the two of you did, at the top of the reveal.
 *
 * The answer to the question the game asks, and it used to be absent entirely: a revealed round
 * opened on question one, and the only way to learn how it went was to scroll five questions
 * counting verdicts.
 *
 * COUNTED FROM THE ROUND'S OWN QUESTIONS, not from the list's summary. The list row carries the
 * same three figures — computed server-side over the same answers — but this view already holds
 * the per-question truth it is summarising, and a total added up from something other than what is
 * drawn under it is a total that can disagree with the page it sits on.
 *
 * "predicts" and not "questions": compares are not scored and never were, and a round of five that
 * announced a score out of five would be pricing the three that are only there to be read.
 */
function Scoreline({
  session,
  partnerName,
  look,
  hitPoints,
  moment,
}: {
  session: SyncSession;
  partnerName: string;
  look: 'bouquet' | 'tuxedo';
  hitPoints: number;
  moment: boolean;
}) {
  const predictions = (session.questions ?? []).filter((q) => q.type === 'predict');
  const mine = predictions.filter((q) => q.myHit).length;
  const theirs = predictions.filter((q) => q.partnerHit).length;
  const predicts = predictions.length;
  return (
    <Card moment={moment}>
      <CardContent className="gap-1">
        <Text variant="caption" className="text-muted-foreground">
          How you did
        </Text>
        <Text variant="display" className={cn('tabular-nums', look === 'bouquet' ? 'italic' : 'fit-opsz')}>
          You {mine} · {partnerName} {theirs}
        </Text>
        <Text variant="small" className="text-muted-foreground">
          out of {predicts} {predicts === 1 ? 'predict' : 'predicts'} · {hitPoints} points a hit
        </Text>
      </CardContent>
    </Card>
  );
}

/**
 * A question, on a card with a SLIM header.
 *
 * It used to wear the full header band — 1370px of red across a desktop window carrying one small
 * badge, five times a round, which turns the reveal into a striped page with answers between the
 * stripes. The band is a titled card's first row and this card has no title: what it has is a
 * chip and a counter, so they sit on the sheet in a quiet row with a hairline under them.
 */
function QuestionCard({
  type,
  counter,
  text,
  children,
}: {
  type: 'compare' | 'predict';
  counter?: string;
  text: string;
  children?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent>
        <View className="border-border flex-row flex-wrap items-center justify-between gap-2 border-b pb-3">
          <QuestionType type={type} />
          {counter ? <Text variant="caption" className="text-muted-foreground">{counter}</Text> : null}
        </View>
        <QuestionText>{text}</QuestionText>
        {children}
      </CardContent>
    </Card>
  );
}

/**
 * The kind of question, named with a glyph and a word.
 *
 * "worth points" is the whole point of the predict badge: the two types feel identical to answer,
 * and only one of them puts points on the week. `PREDICT · SCORES` was tracked capitals saying
 * something in a voice nobody uses — "scores" is a verb with no subject there, and the badge is
 * the one place the reader has to understand a rule before answering.
 *
 * `outline` and not `band`, now that the card under it has no band to ride on. A predict takes the
 * gold that means reward everywhere else in this app, because that is exactly what it is offering.
 */
function QuestionType({ type }: { type: 'compare' | 'predict' }) {
  return (
    <Badge variant={type === 'predict' ? 'success' : 'outline'}>
      <Text>{type === 'predict' ? '◎ Predict · worth points' : '⇄ Compare'}</Text>
    </Badge>
  );
}

/**
 * The question itself: 21px Fraunces italic, on the card under its header row.
 *
 * The one place italic appears in Tuxedo, and it appears there in Bouquet too — so this is not a
 * look branch, it is the shape a question has in both rooms. Not a `CardTitle`: a question is the
 * card's body copy set large, and the heading step is 19px upright, which reads as a section label
 * rather than as something being asked of you.
 */
function QuestionText({ children }: { children: string }) {
  return (
    <Text className="font-display italic text-foreground text-[21px] font-medium leading-[27px]">{children}</Text>
  );
}

/**
 * One answer, as a chat bubble in that partner's identity tint.
 *
 * Yours sits left and theirs right, which is the whole of the "who said this" cue — the tint
 * repeats it in colour for anyone reading the pair at a glance, and the caption repeats it in
 * words for anyone who cannot use either. The tail is the squared bottom corner on the speaker's
 * side rather than a drawn triangle: an overlapping tail shape over a bordered bubble would leave
 * a visible seam where the two edges meet.
 *
 * THE GROUND IS THE ONE THING THE TWO LOOKS DISAGREE ABOUT. Bouquet fills the bubble with the
 * speaker's own `soft` wash and outlines it in their `line`; Tuxedo fills BOTH with `card-2` and
 * lets the outline and the `who` caption carry the identity alone — its room is monochrome, and a
 * pair of tinted washes in it would be the only colour on the page that is neither the red nor the
 * gold. Which is why the fill is a look branch and the border never is.
 *
 * The tail corner is 2px and the bubble 8px, off the restyle's own scale: `rounded-md` is 8 here
 * and `rounded-sm` is 4, so the tail is written as an arbitrary 2 rather than as a step — the
 * sheets cut it to 2 in both looks precisely so it still reads against an 8px shoulder.
 *
 * The guess line is muted whether the guess landed or not, and it NAMES ITS OWNER: "Gabi guessed
 * … ✓ +2". Colour here is IDENTITY — a success green on a partner's bubble reads as a third person
 * in the conversation — and the name is what stops the reader having to work out which of the
 * three "+2"s on a revealed screen belongs to whom.
 */
function Bubble({
  who,
  tint,
  side,
  answer,
  guess,
  hit,
  hitPoints,
}: {
  who: string;
  tint: PartnerId;
  side: 'mine' | 'theirs';
  answer: string | null;
  guess?: string | null;
  hit?: boolean;
  hitPoints: number;
}) {
  // Both read at render: the gels are look- and mode-aware, and a bubble that cached them would
  // keep yesterday's room after somebody switches theme in Settings.
  const look = useStore((s) => s.look);
  const colours = partnerTint(tint);
  const isMine = side === 'mine';
  const tuxedo = look === 'tuxedo';
  return (
    <View className={cn('max-w-[85%] gap-1', isMine ? 'items-start self-start' : 'items-end self-end')}>
      <Text variant="caption" style={{ color: colours.type }}>
        {who}
      </Text>
      <View
        style={tuxedo ? { borderColor: colours.line } : { backgroundColor: colours.soft, borderColor: colours.line }}
        className={cn(
          'gap-1 rounded-md border px-3 py-2',
          tuxedo && 'bg-card-2',
          isMine ? 'rounded-bl-[2px]' : 'rounded-br-[2px]',
        )}>
        <Text>{answer ?? '—'}</Text>
        {guess ? (
          <Text variant="small" className="text-muted-foreground">
            {who} guessed “{guess}” {hit ? `✓ +${hitPoints}` : '✗'}
          </Text>
        ) : null}
      </View>
    </View>
  );
}
